import React, { useRef, useEffect, useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { SSOConfig } from '../../models/sso-config-interface';
import { Application } from '../../constants/Application';
import CloseableErrorMessage from '../CloseableErrorMessage';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { processParsedXml } from '../../utilites/ssoHelper';

interface MetadataUrlModalProps {
  isModalVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  onSubmit: (values: SSOConfig) => void;
  applicationId: Application;
}

const validationSchema = Yup.object({
  metadataUrl: Yup.string().url('Must be a valid URL').required('Metadata URL is required'),
});

const MetadataUrlModal: React.FC<MetadataUrlModalProps> = ({ 
    isModalVisible,
    setIsModalVisible,
    onSubmit,
    applicationId
 }) => {
    const submitFormRef = useRef<(() => void) | null>(null);
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [shouldFetch, setShouldFetch] = useState<boolean>(false);

    const { data: xmlContent, isFetching, error } = CustomerServiceQueries.useGetXmlFromUrl(currentUrl, shouldFetch);

    // Handle XML processing when data is received
    useEffect(() => {
        if (xmlContent && currentUrl && shouldFetch) {
            const ssoConfig = processParsedXml(xmlContent);
            if (!ssoConfig) {
                CloseableErrorMessage(
                    'There was an issue retrieving the configuration data from the URL provided. Please try again, or download the file from the supplied link and upload the metadata file.'
                );
                setShouldFetch(false);
                return;
            }
            onSubmit({ ...ssoConfig, applicationId: applicationId });
            setShouldFetch(false);
        }
    }, [xmlContent, currentUrl, shouldFetch, applicationId, onSubmit]);

    // Handle errors
    useEffect(() => {
        if (error && shouldFetch) {
            CloseableErrorMessage('Error retrieving data from this URL.');
            setShouldFetch(false);
        }
    }, [error, shouldFetch]);

    const handleSubmit = async (values: { metadataUrl: string }) => {
        if (!values.metadataUrl) return;
        setCurrentUrl(values.metadataUrl);
        setShouldFetch(true);
    };

    return (
        <Modal
            visible={isModalVisible}
            destroyOnClose
            onCancel={isFetching ? undefined : () => setIsModalVisible(false)}
            closeIcon={
              <Button
                icon={<FontAwesomeIcon icon={faTimes as IconProp} />}
                disabled={isFetching}
              >
              </Button>
            }
            footer={[
                <Button 
                    key="cancel"
                    type="default" 
                    disabled={isFetching}
                    onClick={() => setIsModalVisible(false)}
                >
                    Cancel
                </Button>,
                <Button 
                    key="submit"
                    type="primary" 
                    loading={isFetching}
                    onClick={() => {
                        // Trigger form submission using stored submit function
                        if (submitFormRef.current) {
                            submitFormRef.current();
                        }
                    }}
                >
                    Submit
                </Button>
            ]}
            title="Import SSO Configuration from Metadata URL"
        >
            <div style={{ marginBottom: 24 }}>
                Add or paste the Metadata URL below and click submit to continue setting up Single Sign-On.
            </div>

            <Formik
                initialValues={{ metadataUrl: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit: formikHandleSubmit }) => {
                    // Store the submit function in ref
                    submitFormRef.current = formikHandleSubmit;
                    
                    return (
                        <Form layout="vertical">
                            <Form.Item
                                label="Metadata URL"
                                validateStatus={errors.metadataUrl && touched.metadataUrl ? 'error' : ''}
                                help={errors.metadataUrl && touched.metadataUrl ? errors.metadataUrl : ''}
                            >
                                <Input
                                    name="metadataUrl"
                                    value={values.metadataUrl}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter metadata URL"
                                    disabled={isFetching}
                                />
                            </Form.Item>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default MetadataUrlModal;
